import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, ArrowRight } from 'lucide-react';
import { realDroneInventory } from '@/data/realDroneInventory';
import { formatPrice } from '@/utils/priceFormatter';
import { useNavigate } from 'react-router-dom';

const categories = [
  'All Categories',
  'Inspection',
  'Surveying',
  'Cinematography',
  'Security',
  'Agriculture',
  'Search & Rescue',
];

export default function FeaturedListingsSection() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All Categories');

  const filteredListings =
    selectedCategory === 'All Categories'
      ? realDroneInventory.filter(d => d.featured)
      : realDroneInventory.filter((listing) => listing.category === selectedCategory && listing.featured);

  return (
    <section className="py-20 sm:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Featured Drone Listings
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Browse verified industrial UAVs from trusted vendors worldwide
            </p>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 justify-center mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="transition-all"
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Listings grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filteredListings.slice(0, 6).map((listing, index) => (
              <Card
                key={listing.id}
                className={`group overflow-hidden hover:shadow-xl transition-all duration-300 border-border hover:border-primary/50 flex flex-col cursor-pointer animate-slide-up stagger-${(index % 6) + 1}`}
                onClick={() => navigate(`/drone/${listing.id}`)}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden bg-muted">
                  <img
                    src={listing.image}
                    alt={listing.model}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    {listing.featured && (
                      <Badge className="bg-accent text-accent-foreground">Featured</Badge>
                    )}
                    <Badge variant="secondary">{listing.category}</Badge>
                  </div>
                </div>

                <CardHeader className="flex-grow">
                  <CardTitle className="text-xl line-clamp-2 group-hover:text-primary transition-colors">
                    {listing.model}
                  </CardTitle>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {listing.location}
                  </div>
                  <CardDescription className="line-clamp-2 mt-2">
                    {listing.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {/* Specs */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">
                      {listing.specs.flightTime}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {listing.specs.payload}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {listing.category}
                    </Badge>
                  </div>
                </CardContent>

                <CardFooter className="flex items-center justify-between pt-4 border-t mt-auto">
                  <div>
                    <div className="text-2xl font-bold text-primary">
                      {formatPrice(listing.price, listing.currency)}
                    </div>
                    <div className="text-xs text-muted-foreground">{listing.vendor}</div>
                  </div>
                  <Button
                    size="sm"
                    className="group/btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/drone/${listing.id}`);
                    }}
                  >
                    View Details
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* View all button */}
          <div className="text-center mt-12">
            <Button size="lg" variant="outline" className="group" onClick={() => navigate('/marketplace')}>
              View All Listings
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}